import {
    useCallback,
    useEffect,
    useState
} from 'react';
import {
    batch,
    useDispatch,
    useSelector
} from 'react-redux';

import {
    AUTH_STATE_NAME,
    fetchPostSessionsRequest,
    getAuthTokensFromParams,
    getLoginCredentialsFromParams,
    useSelectorAuthState
} from '../../../../auth';
import {
    setConfigurations,
    useSelectorConfigurationState
} from '../../../../configuration';
import {
    FetchSuccessAction,
    getFetchSuccessActionType
} from '../../../../middleware';
import { ProductId } from '../../../../product-selector';
import { setItem } from '../../../../storage/use-session-storage/session-storage-helpers';
import { AppState } from '../../../index';
import { handleLogoutRedirect } from '../../effects-middleware/effects';
import { initializeApp } from '../index';
import { deleteUrlParams } from '../utils/url-params-utils';
import { useQueryParams } from '@hooks';

const useConfigurationData = () => {
    const dispatch = useDispatch();
    const { data: configurationData } = useSelectorConfigurationState();

    useEffect(() => {
        if (configurationData === null) {
            dispatch(setConfigurations());
        }
    }, [configurationData, dispatch]);
};
const useAuthData = () => {
    const dispatch = useDispatch();
    const params = useQueryParams();
    const { data: configurationData } = useSelectorConfigurationState();
    const {
        data: authData,
        isLoading: authIsLoading
    } = useSelectorAuthState();
    const handleAuthData = useCallback(() => {
        const authTokens = getAuthTokensFromParams(params);
        const loginCredentials = getLoginCredentialsFromParams(params);

        if (authTokens) {
            const action: FetchSuccessAction = {
                type: getFetchSuccessActionType(AUTH_STATE_NAME),
                meta: { response: {} as any },
                payload: { data: authTokens }
            };

            dispatch(action);
        } else if (loginCredentials && !authIsLoading) {
            dispatch(fetchPostSessionsRequest({
                userName: loginCredentials.username,
                password: loginCredentials.password,
                env: loginCredentials.env || 'qa'
            }));
        }
    }, [dispatch, params, authIsLoading]);

    useEffect(() => {
        if (authData === null) {
            handleAuthData();
        }
    }, [configurationData, authData, handleAuthData]);
};
const useHandleAppInitialization = ({
    setShowTestLogin,
    setIsLoading
}: {
    setShowTestLogin: (updatedShowTestLogin: boolean) => void
    setIsLoading: (updatedIsLoading: boolean) => void
}) => {
    const dispatch = useDispatch();
    const params = useQueryParams();
    const { data: configurationData } = useSelectorConfigurationState();
    const { data: authData } = useSelectorAuthState();
    const configIsReady = configurationData !== null;
    const authIsReady = authData !== null;
    const isReady = configIsReady && authIsReady;
    const handleOpenApp = useCallback(() => {
        const logout_return_to = params.logout_return_to;

        setItem({
            key: 'logout_return_to',
            value: logout_return_to
        });

        deleteUrlParams();

        const paramsProductId = params.productId
            ? parseInt(params.productId, 10)
            : ProductId.mathletics;

        batch(() => {
            if (configurationData !== null) {
                dispatch(initializeApp({
                    isInitialized: true,
                    currentProductId: paramsProductId
                }));
                setShowTestLogin(false);
                setIsLoading(false);
            }
        });
    }, [params.productId, configurationData, dispatch, setShowTestLogin, setIsLoading]);

    useEffect(() => {
        if (isReady) handleOpenApp();
    }, [authData, configurationData, handleOpenApp, isReady]);
};
const useHandleLogout = () => {
    const state = useSelector<AppState, AppState>((state => state));
    const dispatch = useDispatch();
    const { isError: authIsError } = useSelectorAuthState();

    useEffect(() => {
        if (authIsError) {
            handleLogoutRedirect({
                state,
                dispatch
            });
        }
    }, [authIsError, state, dispatch]);
};

export const useAppInitialization = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [showTestLogin, setShowTestLogin] = useState(false);

    useConfigurationData();
    useAuthData();
    useHandleAppInitialization({
        setShowTestLogin,
        setIsLoading
    });
    useHandleLogout();

    return {
        isLoading,
        showTestLogin
    };
};
